// Shapes returned by the coupon/promo admin API. Both coupons and standard
// promos are serialized from the same backend DTO family (CouponPromoDto /
// PromoDto), so a single type covers both — the coupon-only fields are null
// for standard promos.

export type PromoDto = {
  name: string;
  description: string;
  promoId: string;
  startDate: string;
  endDate: string;
  active: boolean;
  lowerAge: number;
  upperAge: number;
  lowerPrice: number;
  upperPrice: number;
  gender: string;
  type: string;
  product: string;
  detail: number;
  percentage: number | null;
  startMonth: number | null;
  couponCode: string | null;
  couponUsageLimit: number | null;
  couponUses: number | null;
  couponConditions: string | null;
  couponExceptions: string | null;
};

export type PromoListResponse = {
  items: PromoDto[];
};

export type CouponLead = {
  ulid: string | null;
  fullname: string;
  email: string | null;
  status: string | null;
  createdAt: string | null;
};

export type CouponLeadsResponse = {
  items: CouponLead[];
};

export type CouponLinkResponse = {
  link: string;
};

export const GENDER_OPTIONS = [
  { value: "B", label: "Ambos" },
  { value: "M", label: "Hombre" },
  { value: "F", label: "Mujer" },
] as const;

export const PRODUCT_OPTIONS = [
  { value: "ALL", label: "Todos" },
  { value: "BASIC", label: "Basic" },
  { value: "PREMIUM", label: "Premium" },
] as const;

// Discount type values accepted by the promo domain (PromoDefinition).
export const TYPE_OPTIONS = [
  { value: "STARTING_MONTHS", label: "Meses iniciales (%)" },
  { value: "FIXED_AMOUNT_MONTHLY", label: "Importe fijo mensual" },
  { value: "NEW_AMOUNT_MONTHLY", label: "Nuevo importe mensual" },
] as const;

// Markets offered when creating a promo (mirrors the legacy PromoAddType).
export const MARKET_OPTIONS = [
  { value: "ALL_MARKETS", label: "Todos los mercados" },
  { value: "ES", label: "España" },
  { value: "AXA_WL_ES", label: "AXA WL (ES)" },
  { value: "FR", label: "Francia" },
] as const;

const LABEL_BY_VALUE = (
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string,
): string => options.find((o) => o.value === value)?.label ?? value;

export const genderLabel = (value: string): string =>
  LABEL_BY_VALUE(GENDER_OPTIONS, value);
export const productLabel = (value: string): string =>
  LABEL_BY_VALUE(PRODUCT_OPTIONS, value);
export const typeLabel = (value: string): string =>
  LABEL_BY_VALUE(TYPE_OPTIONS, value);

export const formatDefinition = (p: PromoDto): string => {
  const parts: string[] = [typeLabel(p.type)];
  if (p.percentage !== null) parts.push(`${p.percentage}`);
  parts.push(`detalle ${p.detail}`);
  if (p.startMonth !== null) parts.push(`mes ${p.startMonth}`);
  return parts.join(" · ");
};

export const formatRange = (lower: number, upper: number): string =>
  `${lower} – ${upper}`;

// Formatters hoisted to module scope: constructing an Intl.DateTimeFormat is
// relatively costly, so we build each once instead of per call (fmtDate/
// fmtDateTime run per row in the timeline and the calls log). The try/catch on
// each caller still guards against an invalid ISO string throwing at .format().
const DATE_FMT = new Intl.DateTimeFormat("es-ES", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const DATE_TIME_FMT = new Intl.DateTimeFormat("es-ES", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return DATE_FMT.format(new Date(iso));
  } catch {
    return iso;
  }
};

// Fecha + hora en local (es-ES). Las vistas del CRM (timeline, llamadas) necesitan
// la hora, pues hay varios eventos el mismo día; fmtDate (solo día) no basta.
// Compartida para que LeadTimeline y la tab de Llamadas la formateen igual.
export const fmtDateTime = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return DATE_TIME_FMT.format(new Date(iso));
  } catch {
    return iso;
  }
};
